import { RouterLink } from '@/common/components/router-links/RouterLink';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { z } from 'zod';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { useNavigate } from '@tanstack/react-router';
import { AccountService } from '@/features/auth/services/AccountService';
import { SelectItem } from '@/common/components/ui/select';
import { useLingui } from '@lingui/react';
import { AvailableLocale } from '@/lib/localization';

const signUpSchema = z.object({
  firstName: requiredInput(z.string().min(1)),
  lastName: requiredInput(z.string().min(1)),
  email: requiredInput(z.email()),
  password: requiredInput(z.string().min(1)),
  confirmPassword: requiredInput(z.string().min(1)),
  language: z.enum(['cs', 'en']),
});

export const SignUpPage = () => {
  const navigate = useNavigate();
  const { i18n } = useLingui();

  const form = useAppForm(
    appFormOptions({
      schema: signUpSchema,
      defaultValues: {
        firstName: null,
        lastName: null,
        email: null,
        password: null,
        confirmPassword: null,
        language: i18n.locale as AvailableLocale,
      },
      onSubmit: async ({ value }) => {
        const { confirmPassword: _, ...registerData } = value;
        await AccountService.register(registerData);
        await navigate({ to: '/verify-email', search: { email: value.email } });
      },
    })
  );

  return (
    <>
      <div className="mb-8 flex flex-col items-center gap-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          <Trans>Create account</Trans>
        </h1>
        <p className="text-sm text-gray-500">
          <Trans>Enter your details to get started</Trans>
        </p>
      </div>

      <form.AppForm>
        <form.FormElement>
          <FormErrors className="mb-4" />

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <form.AppField name="firstName">
                {(field) => (
                  <field.TextField label={<Trans>First name</Trans>} placeholder="Jana" />
                )}
              </form.AppField>

              <form.AppField name="lastName">
                {(field) => (
                  <field.TextField label={<Trans>Last name</Trans>} placeholder="Nováková" />
                )}
              </form.AppField>
            </div>

            <form.AppField name="email">
              {(field) => (
                <field.TextField
                  label={<Trans>Email</Trans>}
                  type="email"
                  placeholder="name@company.com"
                />
              )}
            </form.AppField>

            <form.AppField name="password">
              {(field) => <field.TextField label={<Trans>Password</Trans>} type="password" />}
            </form.AppField>

            <form.AppField
              name="confirmPassword"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  const password = fieldApi.form.getFieldValue('password');
                  if (value !== null && value !== password) {
                    return t`Passwords do not match`;
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <field.TextField label={<Trans>Confirm password</Trans>} type="password" />
              )}
            </form.AppField>

            <form.AppField name="language">
              {(field) => (
                <field.SelectField label={<Trans>Preferred language</Trans>}>
                  <SelectItem value="cs">
                    <Trans>Czech</Trans>
                  </SelectItem>
                  <SelectItem value="en">
                    <Trans>English</Trans>
                  </SelectItem>
                </field.SelectField>
              )}
            </form.AppField>
          </div>

          <FormSubmit className="mt-6 w-full">
            <Trans>Create account</Trans>
          </FormSubmit>
        </form.FormElement>
      </form.AppForm>

      <p className="mt-6 text-center text-sm text-gray-500">
        <Trans>Already have an account?</Trans>{' '}
        <RouterLink to={'/login'}>
          <Trans>Sign in</Trans>
        </RouterLink>
      </p>
    </>
  );
};
